import config from '../config';
import selectedEnvironment from '../env';

const baseURL = config[selectedEnvironment].API_URL;

const handleResponse = async (response, errorMessage) => {
    if (!response.ok) {
        const text = await response.text();
        throw new Error(`${errorMessage}: ${text}`);
    }
    return response.json(); // envelope: { success, data }
};

const SortCodes_API_URL = `${baseURL}/SortCodes`;
const SortCodesGroup_API_URL = `${baseURL}/SortCodesGroup`;
const Providers_API_URL = `${baseURL}/Providers`;
const OtherDocumentsTypes_API_URL = `${baseURL}/OtherDocumentsTypes`;
const OtherDocumentsTitles_API_URL = `${baseURL}/OtherDocumentsTitles`;
const OtherDocuments_API_URL = `${baseURL}/OtherDocuments`;
const ChecksTypes_API_URL = `${baseURL}/ChecksTypes`;
const CaseTypes_API_URL = `${baseURL}/CaseTypes`;
const AccountCards_API_URL = `${baseURL}/AccountCards`;
const ClientItems_API_URL = `${baseURL}/ClientItems`;
const PalCodeCategories_API_URL = `${baseURL}/PalCodeCategories`;
const PalCodes_API_URL = `${baseURL}/PalCodes`;
const PalCodeAccountingGroups_API_URL = `${baseURL}/PalCodeAccountingGroups`;
const Code6111_API_URL = `${baseURL}/CodeSixOneOneOne`;
const Code1214_API_URL = `${baseURL}/CodeTwelveFourteen`;
const TBanks_API_URL = `${baseURL}/TBanks`;
const Ledgers_API_URL = `${baseURL}/Ledgers`;
const PaymentAcceptance_API_URL = `${baseURL}/PaymentAcceptances`;
const Accountants_API_URL = `${baseURL}/Accountants`;

const API_URL = baseURL;

const GetAccountCardsByCaseId = async (CaseId, auth) => {
    const response = await fetch(`${AccountCards_API_URL}/ByCaseId/${CaseId}`, {
        method: 'GET',
        headers: {
            "Authorization": 'Bearer ' + auth,
            "Content-Type": "application/json"
        },
    });

    return response;
};

const GetReservedAccountCardIds = async (auth) => {
    const response = await fetch(`${AccountCards_API_URL}/ReservedCardIds`, {
        method: 'GET',
        headers: {
            "Authorization": 'Bearer ' + auth,
            "Content-Type": "application/json"
        },
    });
    return response;
};

const GetReservedAccountCardIdsByCase = async (caseId, auth) => {
    const response = await fetch(`${AccountCards_API_URL}/ReservedCardIds/${encodeURIComponent(caseId)}`, {
        method: 'GET',
        headers: {
            "Authorization": 'Bearer ' + auth,
            "Content-Type": "application/json"
        },
    });
    return response;
};

const GetAccountCardIdsWithLinkedDocuments = async (caseId, auth) => {
    const response = await fetch(
        `${AccountCards_API_URL}/CardIdsWithLinkedDocuments/${encodeURIComponent(caseId)}`,
        {
            method: 'GET',
            headers: {
                Authorization: 'Bearer ' + auth,
                'Content-Type': 'application/json',
            },
        }
    );
    return response;
};

const GetAccountCardsWithoutTransactions = async (caseId, auth) => {
    const response = await fetch(
        `${AccountCards_API_URL}/WithoutTransactions/${encodeURIComponent(caseId)}`,
        {
            method: 'GET',
            headers: {
                Authorization: 'Bearer ' + auth,
                'Content-Type': 'application/json',
            },
        }
    );
    return response;
};

const GetCaseSystemAccountSettings = async (caseId, auth) => {
    const response = await fetch(`${AccountCards_API_URL}/SystemAccountSettings/${encodeURIComponent(caseId)}`, {
        method: 'GET',
        headers: {
            "Authorization": 'Bearer ' + auth,
            "Content-Type": "application/json"
        },
    });
    return response;
};

const UpdateCaseSystemAccountSettings = async (caseId, payload, auth) => {
    const response = await fetch(`${AccountCards_API_URL}/SystemAccountSettings/${encodeURIComponent(caseId)}`, {
        method: 'PUT',
        headers: {
            "Authorization": 'Bearer ' + auth,
            "Content-Type": "application/json"
        },
        body: JSON.stringify(payload),
    });
    return response;
};

const GetAccountCardByCardId = async (CaseId, CardId, auth) => {
    const response = await fetch(`${AccountCards_API_URL}/GetAccountCardByCardId`, {
        method: 'POST',
        headers: {
            "Authorization": 'Bearer ' + auth,
            "Content-Type": "application/json"
        },
        body: JSON.stringify({
            CaseId: CaseId,
            CardId: CardId
        })
    });

    return response;
};

const GetNextCardId = async (caseId, palCodeId, auth) => {
    const params = new URLSearchParams({ caseId });
    if (palCodeId) params.set('palCodeId', palCodeId);
    const response = await fetch(`${AccountCards_API_URL}/NextCardId?${params}`, {
        method: 'GET',
        headers: {
            "Authorization": 'Bearer ' + auth,
            "Content-Type": "application/json"
        },
    });
    if (!response.ok) {
        throw new Error('Failed to get next card ID');
    }
    const data = await response.json();
    return data.cardId;
};

const GetCaseTypesByByAccountantId = async (AccountantId, auth) => {
    const response = await fetch(`${CaseTypes_API_URL}/ByAccountantId/${AccountantId}`, {
        method: 'GET',
        headers: {
            "Authorization": 'Bearer ' + auth,
            "Content-Type": "application/json"
        },
    });

    return response;
};

const GetChecksTypesByAccountantId = async (AccountantId, auth) => {
    const response = await fetch(`${ChecksTypes_API_URL}/ByAccountantId/${AccountantId}`, {
        method: 'GET',
        headers: {
            "Authorization": 'Bearer ' + auth,
            "Content-Type": "application/json"
        },
    });

    return response;
};

const GetOtherDocumentsTypesByAccountantId = async (AccountantId, auth) => {
    const response = await fetch(`${OtherDocumentsTypes_API_URL}/ByAccountantId/${AccountantId}`, {
        method: 'GET',
        headers: {
            "Authorization": 'Bearer ' + auth,
            "Content-Type": "application/json"
        },
    });

    return response;
};

const GetOtherDocumentsCountsByCaseId = async (caseId, auth) => {

    const response = await fetch(`${OtherDocuments_API_URL}/GetOtherDocumentCountsByType/${caseId}`, {
        method: 'GET',
        headers: {
            "Authorization": `Bearer ${auth}`,
            "Content-Type": "application/json"
        },
    });

    return response; // Parse and return the JSON response
};


const GetProvidersBycaseId = async (CaseId, auth) => {
    const response = await fetch(`${Providers_API_URL}/GetAllBycaseId/${CaseId}`, {
        method: 'GET',
        headers: {
            "Authorization": 'Bearer ' + auth,
            "Content-Type": "application/json"
        },
    });

    return response;
};

const GetSortCodesByAccountantId = async (accountantId, auth) => {
    const response = await fetch(`${SortCodes_API_URL}/ByAccountantId/${accountantId}`, {
        method: 'GET',
        headers: {
            "Authorization": 'Bearer ' + auth,
            "Content-Type": "application/json"
        },
    });

    return response;
};

const GetSortCodesGroupByAccountantId = async (accountantId, auth) => {
    const response = await fetch(`${SortCodesGroup_API_URL}/ByAccountantId/${accountantId}`, {
        method: 'GET',
        headers: {
            "Authorization": 'Bearer ' + auth,
            "Content-Type": "application/json"
        },
    });

    return response;
};
const AddProvider = async (CaseId, ProviderId, ProviderName, auth) => {
    const response = await fetch(`${Providers_API_URL}/Create`, {
        method: 'POST',
        headers: {
            "Authorization": 'Bearer ' + auth,
            "Content-Type": "application/json"
        },
        body: JSON.stringify({ CaseId, ProviderId, ProviderName })
    });

    return response;
};

const AddAccountCard = async (CaseId, CardId, Name, AccountCaseId, PalCode, auth, phoneNumber = null, location = null, email = null, sourceDeductionAssociation = null,
    IsEditable = true, ExactOccupation = null, DeductionType = null, TaxAssessor = null, DeductionValidity = null, DeductionRate = null, ExternalSoftwareCardNumber = null, SortCodeId = null, AutoProcessed = false, Documents = null,
    PaymentTermsType = null, PaymentTermsCustomDays = null, Frequency = null, OcrRoutingEnabled = false, OcrRoutingRules = null,
    // { IncludeInCashFlow, CashFlowCode, CashFlowAverageAmount, CashFlowCurrency } — Cash Flow Forecast settings.
    CashFlowConfig = null,
    DeliveryLocation = null,
    // { PhoneNumber2, BankCode, BankName, BranchCode, AccountNumber }
    AdditionalDetails = null) => {
    const response = await fetch(`${AccountCards_API_URL}/Create`, {
        method: 'POST',
        headers: {
            "Authorization": 'Bearer ' + auth,
            "Content-Type": "application/json"
        },
        body: JSON.stringify({ CaseId, CardId, Name, AccountCaseId, PalCode, phoneNumber, location, DeliveryLocation, email, sourceDeductionAssociation, IsEditable, ExactOccupation, DeductionType, TaxAssessor, DeductionValidity, DeductionRate, ExternalSoftwareCardNumber, SortCodeId, AutoProcessed, Documents, PaymentTermsType, PaymentTermsCustomDays, Frequency, OcrRoutingEnabled, OcrRoutingRules, ...(CashFlowConfig || {}), ...(AdditionalDetails || {}) })
    });

    return response;
};

const UpdateAccountCardCaseId = async (CaseId, CardId, AccountCaseId, email, phoneNumber, location, auth, deliveryLocation = null) => {
    const OldCardId = CardId;
    
    const response = await fetch(`${AccountCards_API_URL}/Update`, {
        method: 'PUT',
        headers: {
            "Authorization": 'Bearer ' + auth,
            "Content-Type": "application/json"
        },
        body: JSON.stringify({
            CaseId,
            CardId,
            OldCardId,
            AccountCaseId,
            email,
            phoneNumber,
            location,
            ...(deliveryLocation !== null && deliveryLocation !== undefined
                ? { DeliveryLocation: deliveryLocation }
                : {}),
        })
    });
    return response;
};
const UpdateAccountCardBankDetails = async (CaseId, CardId, { BankCode, BankName, BranchCode, AccountNumber }, auth) => {
    const response = await fetch(`${AccountCards_API_URL}/UpdateBankDetails`, {
        method: 'PUT',
        headers: {
            "Authorization": 'Bearer ' + auth,
            "Content-Type": "application/json"
        },
        body: JSON.stringify({ CaseId, CardId: String(CardId), BankCode, BankName, BranchCode, AccountNumber })
    });
    return response;
};

const EditInBulk = async (CaseId, AccountCardCodes, NewPalCode, auth) => {
    const response = await fetch(`${AccountCards_API_URL}/EditInBulk`, {
        method: 'PUT',
        headers: {
            "Authorization": 'Bearer ' + auth,
            "Content-Type": "application/json"
        },
        body: JSON.stringify({ CaseId, AccountCardCodes, NewPalCode })
    });

    return response;
};

const EditSortCodeInBulk = async (CaseId, AccountCardCodes, SortCodeId, AutoProcessed, auth) => {
    const response = await fetch(`${AccountCards_API_URL}/EditSortCodeInBulk`, {
        method: 'PUT',
        headers: {
            "Authorization": 'Bearer ' + auth,
            "Content-Type": "application/json"
        },
        body: JSON.stringify({ CaseId, AccountCardCodes, SortCodeId, AutoProcessed })
    });

    return response;
};

const EditDeductionInBulk = async (CaseId, AccountCardCodes, SourceDeductionAssociation, DeductionType, TaxAssessor, DeductionRate, DeductionValidity, auth) => {
    const response = await fetch(`${AccountCards_API_URL}/EditDeductionInBulk`, {
        method: 'PUT',
        headers: {
            "Authorization": 'Bearer ' + auth,
            "Content-Type": "application/json"
        },
        body: JSON.stringify({ CaseId, AccountCardCodes, SourceDeductionAssociation, DeductionType, TaxAssessor, DeductionRate, DeductionValidity })
    });

    return response;
};

const EditPaymentTermsInBulk = async (CaseId, AccountCardCodes, PaymentTermsType, PaymentTermsCustomDays, auth) => {
    const response = await fetch(`${AccountCards_API_URL}/EditPaymentTermsInBulk`, {
        method: 'PUT',
        headers: {
            "Authorization": 'Bearer ' + auth,
            "Content-Type": "application/json"
        },
        body: JSON.stringify({ CaseId, AccountCardCodes, PaymentTermsType, PaymentTermsCustomDays })
    });

    return response;
};


const AddCaseType = async (AccountantId, Name, auth) => {
    const response = await fetch(`${CaseTypes_API_URL}/Create`, {
        method: 'POST',
        headers: {
            "Authorization": 'Bearer ' + auth,
            "Content-Type": "application/json"
        },
        body: JSON.stringify({ AccountantId, Name })
    });

    return response;
};

const AddChecksType = async (AccountantId, Name, auth) => {
    const response = await fetch(`${ChecksTypes_API_URL}/Create`, {
        method: 'POST',
        headers: {
            "Authorization": 'Bearer ' + auth,
            "Content-Type": "application/json"
        },
        body: JSON.stringify({ AccountantId, Name })
    });

    return response;
};

const AddOtherDocumentsType = async (AccountantId, Name, auth, HiddenFromClient = false, TitleId = null) => {
    const response = await fetch(`${OtherDocumentsTypes_API_URL}/Create`, {
        method: 'POST',
        headers: {
            "Authorization": 'Bearer ' + auth,
            "Content-Type": "application/json"
        },
        body: JSON.stringify({ AccountantId, Name, HiddenFromClient, TitleId: TitleId || null })
    });

    return response;
};

const GetOtherDocumentsTitlesByAccountantId = async (AccountantId, auth) => {
    const response = await fetch(`${OtherDocumentsTitles_API_URL}/ByAccountantId/${AccountantId}`, {
        method: 'GET',
        headers: {
            "Authorization": 'Bearer ' + auth,
            "Content-Type": "application/json"
        },
    });

    return response;
};

const AddOtherDocumentsTitle = async (AccountantId, Name, auth, HiddenFromClient = false, TypeIds = [], SortOrder = 0) => {
    const response = await fetch(`${OtherDocumentsTitles_API_URL}/Create`, {
        method: 'POST',
        headers: {
            "Authorization": 'Bearer ' + auth,
            "Content-Type": "application/json"
        },
        body: JSON.stringify({ AccountantId, Name, HiddenFromClient, TypeIds, SortOrder })
    });

    return response;
};

const UpdateOtherDocumentsTitle = async (AccountantId, Id, { name, hiddenFromClient, sortOrder } = {}, auth) => {
    const body = { AccountantId, Id };
    if (name !== undefined) body.Name = name;
    if (hiddenFromClient !== undefined) body.HiddenFromClient = hiddenFromClient;
    if (sortOrder !== undefined) body.SortOrder = sortOrder;
    const response = await fetch(`${OtherDocumentsTitles_API_URL}/Update`, {
        method: 'PUT',
        headers: {
            "Authorization": 'Bearer ' + auth,
            "Content-Type": "application/json"
        },
        body: JSON.stringify(body)
    });

    return response;
};

const DeleteOtherDocumentsTitle = async (Id, auth) => {
    const response = await fetch(`${OtherDocumentsTitles_API_URL}/Delete/${Id}`, {
        method: 'DELETE',
        headers: {
            "Authorization": 'Bearer ' + auth,
            "Content-Type": "application/json"
        },
    });

    return response;
};

// TitleId null moves the types back to the default sections.
const AssignTypesToOtherDocumentsTitle = async (AccountantId, TitleId, TypeIds, auth) => {
    const response = await fetch(`${OtherDocumentsTitles_API_URL}/AssignTypes`, {
        method: 'PUT',
        headers: {
            "Authorization": 'Bearer ' + auth,
            "Content-Type": "application/json"
        },
        body: JSON.stringify({ AccountantId, TitleId: TitleId || null, TypeIds })
    });

    return response;
};

const SetOtherDocumentsTitlesHidden = async (AccountantId, TitleIds, Hidden, auth) => {
    const response = await fetch(`${OtherDocumentsTitles_API_URL}/SetHidden`, {
        method: 'PUT',
        headers: {
            "Authorization": 'Bearer ' + auth,
            "Content-Type": "application/json"
        },
        body: JSON.stringify({ AccountantId, TitleIds, Hidden })
    });

    return response;
};

const EnsurePermanentFileTitle = async (AccountantId, HiddenFromClient, auth) => {
    const response = await fetch(`${OtherDocumentsTitles_API_URL}/EnsurePermanentFile`, {
        method: 'POST',
        headers: {
            "Authorization": 'Bearer ' + auth,
            "Content-Type": "application/json"
        },
        body: JSON.stringify({ AccountantId, HiddenFromClient })
    });

    return response;
};

const AddSortCode = async (AccountantId, SortCodeName, SortCodeId, sortCodeGroup, creditAccount = null, debitAccount = null, SortCodeVatType = null,vat = 100,participatesInProfitAndLoss = true, pcnTransactionType = null, code6111Id = null, inventoryType = 0, auth) => {
    const response = await fetch(`${SortCodes_API_URL}/Create`, {
        method: 'POST',
        headers: {
            "Authorization": 'Bearer ' + auth,
            "Content-Type": "application/json"
        },
        body: JSON.stringify({ AccountantId, SortCodeName, SortCodeId, sortCodeGroup, creditAccount, debitAccount, SortCodeVatType,vat,participatesInProfitAndLoss, pcnTransactionType, code6111Id, inventoryType })
    });

    return response;
};

const AddSortCodeGroup = async (AccountantId, SortCodeGroup, SortCodeListNumber, SortCodeGroupProfitName,category ,auth) => {
    const response = await fetch(`${SortCodesGroup_API_URL}/Create`, {
        method: 'POST',
        headers: {
            "Authorization": 'Bearer ' + auth,
            "Content-Type": "application/json"
        },
        body: JSON.stringify({ AccountantId, SortCodeGroup, SortCodeListNumber, SortCodeGroupProfitName,category:parseInt(category) })
    });

    return response;
};

const UpdateProvider = async (CaseId, newProviderId, ProviderId, ProviderName, auth) => {
    const response = await fetch(`${Providers_API_URL}/Update`, {
        method: 'PUT',
        headers: {
            "Authorization": 'Bearer ' + auth,
            "Content-Type": "application/json"
        },
        body: JSON.stringify({ CaseId, newProviderId, ProviderId, ProviderName })
    });

    return response;
};


const UpdateAccountCard = async (CaseId, OldCardId, OldName, CardId, Name, AccountCaseId, PalCode, auth
    , phoneNumber = null, location = null, email = null, sourceDeductionAssociation = null, oldAccountCaseId = ''
    , isEditable = true, ExactOccupation = null, DeductionType = null, taxAssesor = null, DeductionValidity = null, DeductionRate = null, ExternalSoftwareCardNumber = null, SortCodeId = null, AutoProcessed = false, Documents = null,
    PaymentTermsType = null, PaymentTermsCustomDays = null, Frequency = null, OcrRoutingEnabled = null, OcrRoutingRules = null,
    UpdatePaymentTerms = false,
    // { IncludeInCashFlow, CashFlowCode, CashFlowAverageAmount, CashFlowCurrency, UpdateCashFlowConfig }.
    // Omit it entirely from partial updates so the existing cash-flow settings are kept.
    CashFlowConfig = null,
    DeliveryLocation = null,
    // { PhoneNumber2, BankCode, BankName, BranchCode, AccountNumber }. When passed, they are
    // applied including clears (UpdateAdditionalDetails); omit it to keep the stored values.
    AdditionalDetails = null) => {


    const response = await fetch(`${AccountCards_API_URL}/Update`, {
        method: 'PUT',
        headers: {
            "Authorization": 'Bearer ' + auth,
            "Content-Type": "application/json"
        },
        body: JSON.stringify({
            CaseId, OldCardId, CardId, OldName, Name, AccountCaseId, PalCode,
            phoneNumber, location, DeliveryLocation, email, sourceDeductionAssociation,
            isEditable, oldAccountCaseId, ExactOccupation, DeductionType, TaxAssessor: taxAssesor ? taxAssesor.toString() : null
            , DeductionValidity, DeductionRate, ExternalSoftwareCardNumber, SortCodeId, AutoProcessed, Documents,
            PaymentTermsType, PaymentTermsCustomDays, UpdatePaymentTerms, Frequency, OcrRoutingEnabled, OcrRoutingRules,
            ...(CashFlowConfig || {}),
            ...(AdditionalDetails ? { ...AdditionalDetails, UpdateAdditionalDetails: true } : {})
        })
    });

    return response;
};

const UpdateAccountCardFewDetails = async (CaseId, OldCardId, OldName, CardId, Name, AccountCaseId, oldAccountCaseId, PalCode = '', auth) => {
    const response = await fetch(`${AccountCards_API_URL}/Update`, {
        method: 'PUT',
        headers: {
            "Authorization": 'Bearer ' + auth,
            "Content-Type": "application/json"
        },
        body: JSON.stringify({ CaseId, OldCardId, CardId, OldName, Name, AccountCaseId, PalCode, oldAccountCaseId })
    });

    return response;
};

const MergeAccountCards = async (CaseId, PrimaryCardId, SecondaryCardId, auth) => {
    const response = await fetch(`${AccountCards_API_URL}/Merge`, {
        method: 'PUT',
        headers: {
            "Authorization": 'Bearer ' + auth,
            "Content-Type": "application/json"
        },
        body: JSON.stringify({ CaseId, PrimaryCardId, SecondaryCardId })
    });

    return response;
};

const BatchMergeAccountCards = async (CaseId, MergeOperations, DeleteOldCards, auth) => {
    const response = await fetch(`${AccountCards_API_URL}/BatchMerge`, {
        method: 'PUT',
        headers: {
            "Authorization": 'Bearer ' + auth,
            "Content-Type": "application/json"
        },
        body: JSON.stringify({ CaseId, MergeOperations, DeleteOldCards })
    });

    return response;
};

const GetPalCodePrefixes = async (caseId, auth) => {
    const response = await fetch(`${AccountCards_API_URL}/Prefixes/${encodeURIComponent(caseId)}`, {
        method: 'GET',
        headers: {
            "Authorization": 'Bearer ' + auth,
            "Content-Type": "application/json"
        },
    });
    return response;
};

const SetPalCodePrefix = async (caseId, palCodeId, prefix, auth) => {
    const response = await fetch(`${AccountCards_API_URL}/Prefixes`, {
        method: 'PUT',
        headers: {
            "Authorization": 'Bearer ' + auth,
            "Content-Type": "application/json"
        },
        body: JSON.stringify({ CaseId: caseId, PalCodeId: palCodeId, Prefix: prefix }),
    });
    return response;
};

const GetMergeSuggestions = async (caseId, auth, nameAccuracyMin) => {
    const accuracy = nameAccuracyMin != null ? nameAccuracyMin / 100 : 0.90;
    const response = await fetch(`${AccountCards_API_URL}/MergeSuggestions?caseId=${encodeURIComponent(caseId)}&nameAccuracyMin=${accuracy}`, {
        method: 'GET',
        headers: {
            "Authorization": 'Bearer ' + auth,
            "Content-Type": "application/json"
        },
    });
    return response;
};

const GetSimilarCards = async (caseId, body, auth) => {
    const response = await fetch(`${AccountCards_API_URL}/SimilarCards?caseId=${encodeURIComponent(caseId)}`, {
        method: 'POST',
        headers: {
            "Authorization": 'Bearer ' + auth,
            "Content-Type": "application/json"
        },
        body: JSON.stringify(body),
    });
    return response;
};

const SetAccountCardEditable = async (caseId, cardId, isEditable, auth) => {
    const response = await fetch(`${AccountCards_API_URL}/SetEditable`, {
        method: 'PUT',
        headers: {
            "Authorization": 'Bearer ' + auth,
            "Content-Type": "application/json"
        },
        body: JSON.stringify({ CaseId: caseId, CardId: cardId, IsEditable: isEditable }),
    });
    return response;
};

const UpdateCaseType = async (AccountantId, Name, OldName, auth) => {
    const response = await fetch(`${CaseTypes_API_URL}/Update`, {
        method: 'PUT',
        headers: {
            "Authorization": 'Bearer ' + auth,
            "Content-Type": "application/json"
        },
        body: JSON.stringify({ AccountantId, Name, OldName })
    });

    return response;
};

const UpdateCheckType = async (AccountantId, Name, OldName, auth) => {
    const response = await fetch(`${ChecksTypes_API_URL}/Update`, {
        method: 'PUT',
        headers: {
            "Authorization": 'Bearer ' + auth,
            "Content-Type": "application/json"
        },
        body: JSON.stringify({ AccountantId, Name, OldName })
    });

    return response;
};

const UpdateOtherDocumentsType = async (AccountantId, Name, OldName, auth, HiddenFromClient = undefined) => {
    const body = { AccountantId, Name, OldName };
    if (HiddenFromClient !== undefined) {
        body.HiddenFromClient = HiddenFromClient;
    }
    const response = await fetch(`${OtherDocumentsTypes_API_URL}/Update`, {
        method: 'PUT',
        headers: {
            "Authorization": 'Bearer ' + auth,
            "Content-Type": "application/json"
        },
        body: JSON.stringify(body)
    });

    return response;
};

const UpdateSortCode = async (AccountantId, OldSortCodeId, SortCodeId, SortCodeName, sortCodeGroup, CreditAccount, DebitAccount, sortCodeVatType = null, vat = 100,participatesInProfitAndLoss = true, pcnTransactionType = null, code6111Id = null, updateTransactions = false, inventoryType = 0, auth) => {
    const response = await fetch(`${SortCodes_API_URL}/Update`, {
        method: 'PUT',
        headers: {
            "Authorization": 'Bearer ' + auth,
            "Content-Type": "application/json"
        },
        body: JSON.stringify({ AccountantId, OldSortCodeId, SortCodeId, SortCodeName, sortCodeGroup, CreditAccount, DebitAccount, sortCodeVatType,vat,participatesInProfitAndLoss, pcnTransactionType, code6111Id, updateTransactions, inventoryType })
    });

    return response;
};

const MergeSortCodes = async (AccountantId, PrimarySortCodeId, SecondarySortCodeId, auth) => {
    const response = await fetch(`${SortCodes_API_URL}/Merge`, {
        method: 'PUT',
        headers: {
            "Authorization": 'Bearer ' + auth,
            "Content-Type": "application/json"
        },
        body: JSON.stringify({ AccountantId, PrimarySortCodeId, SecondarySortCodeId })
    });

    return response;
};

const BatchMergeSortCodes = async (AccountantId, MergeOperations, DeleteOldCodes, auth) => {
    const response = await fetch(`${SortCodes_API_URL}/BatchMerge`, {
        method: 'PUT',
        headers: {
            "Authorization": 'Bearer ' + auth,
            "Content-Type": "application/json"
        },
        body: JSON.stringify({ AccountantId, MergeOperations, DeleteOldCodes })
    });

    return response;
};

const UpdateSortCodeGroup = async (AccountantId, SortCodeGroup, SortCodeListNumber, sortCodeGroupProfitName, Id, category,auth) => {
    const response = await fetch(`${SortCodesGroup_API_URL}/Update`, {
        method: 'PUT',
        headers: {
            "Authorization": 'Bearer ' + auth,
            "Content-Type": "application/json"
        },
        body: JSON.stringify({ AccountantId, Id, SortCodeGroup, SortCodeListNumber, sortCodeGroupProfitName,category:parseInt(category) })
    });

    return response;
};

const DeleteAccountCardsbyCaseId = async (CaseId, cardId, auth) => {
    const response = await fetch(`${AccountCards_API_URL}/Delete`, {
        method: 'Delete',
        headers: {
            "Authorization": 'Bearer ' + auth,
            "Content-Type": "application/json"
        },
        body: JSON.stringify({ CaseId, cardId })
    });

    return response;
};

const BulkDeleteAccountCards = async (caseId, cardIds, auth) => {
    const response = await fetch(`${AccountCards_API_URL}/DeleteMany`, {
        method: 'POST',
        headers: {
            Authorization: 'Bearer ' + auth,
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({ caseId, cardIds }),
    });
    return response;
};

const DeleteProviderbyCaseId = async (CaseId, providerId, auth) => {
    const response = await fetch(`${Providers_API_URL}/Delete`, {
        method: 'POST',
        headers: {
            "Authorization": 'Bearer ' + auth,
            "Content-Type": "application/json"
        },
        body: JSON.stringify({ CaseId, providerId })
    });

    return response;
};

const DeleteCaseTypebyId = async (caseTypeId, auth) => {
    const response = await fetch(`${CaseTypes_API_URL}/Delete/${caseTypeId}`, {
        method: 'DELETE',
        headers: {
            "Authorization": 'Bearer ' + auth,
            "Content-Type": "application/json"
        },
    });

    return response;
};

const DeleteChecksTypebyId = async (checkTypeId, auth) => {
    const response = await fetch(`${ChecksTypes_API_URL}/Delete/${checkTypeId}`, {
        method: 'DELETE',
        headers: {
            "Authorization": 'Bearer ' + auth,
            "Content-Type": "application/json"
        },
    });

    return response;
};

const DeleteOtherDocumentbyId = async (otherDocumentId, auth) => {
    const response = await fetch(`${OtherDocumentsTypes_API_URL}/Delete/${otherDocumentId}`, {
        method: 'DELETE',
        headers: {
            "Authorization": 'Bearer ' + auth,
            "Content-Type": "application/json"
        },
    });

    return response;
};

// 409 Conflict ({ documentCount }) when documents still use the type and no reassignTo is given.
const DeleteOtherDocumentsType = async (otherDocumentsTypeId, auth, reassignTo = null) => {
    const query = reassignTo ? `?reassignTo=${encodeURIComponent(reassignTo)}` : '';
    const response = await fetch(`${OtherDocumentsTypes_API_URL}/Delete/${otherDocumentsTypeId}${query}`, {
        method: 'DELETE',
        headers: {
            "Authorization": 'Bearer ' + auth,
            "Content-Type": "application/json"
        },
    });

    return response;
};

// { [lower-cased type name]: document count } across the accountant's clients.
const GetOtherDocumentsTypeUsage = async (AccountantId, auth) => {
    const response = await fetch(`${OtherDocumentsTypes_API_URL}/UsageCounts/${AccountantId}`, {
        method: 'GET',
        headers: {
            "Authorization": 'Bearer ' + auth,
            "Content-Type": "application/json"
        },
    });

    return response;
};

const DeleteSortCodebyId = async (AccountantId, codeId, auth) => {
    const response = await fetch(`${SortCodes_API_URL}/Delete`, {
        method: 'POST',
        headers: {
            "Authorization": 'Bearer ' + auth,
            "Content-Type": "application/json"
        },
        body: JSON.stringify({ AccountantId, codeId })
    });

    return response;
};

const DeleteSortCodeGroupById = async (AccountantId, Id, auth) => {
    const response = await fetch(`${SortCodesGroup_API_URL}/Delete`, {
        method: 'POST',
        headers: {
            "Authorization": 'Bearer ' + auth,
            "Content-Type": "application/json"
        },
        body: JSON.stringify({ AccountantId, Id })
    });

    return response;
};

const UploadAccountCardsFileData = async (caseId, file, auth) => {
    const formData = new FormData();
    formData.append("CaseId", caseId);
    formData.append("File", file);

    const response = await fetch(`${AccountCards_API_URL}/Upload`, {
        method: 'POST',
        headers: {
            "Authorization": `Bearer ${auth}`,
        },
        body: formData
    });

    return response;
};

const UploadFileData = async (type, AccountantId, file, auth) => {
    const formData = new FormData();
    formData.append("AccountantId", AccountantId);
    formData.append("File", file);

    const response = await fetch(`${API_URL}/${type}/Upload`, {
        method: 'POST',
        headers: {
            "Authorization": `Bearer ${auth}`,
        },
        body: formData
    });

    return response;
};

const GetClientItemsByCaseId = async (CaseId, auth) => {
    const response = await fetch(`${ClientItems_API_URL}/ByCaseId/${CaseId}`, {
        method: 'GET',
        headers: {
            "Authorization": 'Bearer ' + auth,
            "Content-Type": "application/json"
        },
    });

    return response;
};

const AddClientItems = async (items, auth) => {
    const response = await fetch(`${ClientItems_API_URL}/Create`, {
        method: 'POST',
        headers: {
            "Authorization": 'Bearer ' + auth,
            "Content-Type": "application/json"
        },
        body: JSON.stringify({ Items: items }) // Items is an array of { CaseId, Makat, Name }
    });

    return response;
};


const UpdateClientItem = async (CaseId, OriginalMakat, Makat, Name, auth) => {
    const response = await fetch(`${ClientItems_API_URL}/Update`, {
        method: 'PUT',
        headers: {
            "Authorization": 'Bearer ' + auth,
            "Content-Type": "application/json"
        },
        body: JSON.stringify({ CaseId, OriginalMakat, Makat, Name })
    });

    return response;
};

const GetSortCodeByName = async (sortCodeName, accountantId, token) => {

    try {
        const response = await fetch(`${SortCodes_API_URL}/GetSortCodeByName`, {
            method: 'POST',
            headers: {
                "Authorization": 'Bearer ' + token,
                "Content-Type": "application/json",
            },
            body: JSON.stringify({ sortCodeName, accountantId })
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        return response;
    } catch (error) {
    }
};

const GetPalCodeCategoriesByAccountantId = async (AccountantId, auth) => {
    const response = await fetch(`${PalCodeCategories_API_URL}/ByAccountantId/${AccountantId}`, {
        method: 'GET',
        headers: {
            "Authorization": 'Bearer ' + auth,
            "Content-Type": "application/json"
        },
    });

    return response;
};

const AddPalCodeCategory = async (newPalCodeCategory, auth) => {
    const response = await fetch(`${PalCodeCategories_API_URL}/Create`, {
        method: 'POST',
        headers: {
            "Authorization": 'Bearer ' + auth,
            "Content-Type": "application/json"
        },
        body: JSON.stringify({ ...newPalCodeCategory })
    });

    return response;
};

const UpdatePalCodeCategory = async (updatedPalCodeCategory, auth) => {
    const response = await fetch(`${PalCodeCategories_API_URL}/Update`, {
        method: 'PUT',
        headers: {
            "Authorization": 'Bearer ' + auth,
            "Content-Type": "application/json"
        },
        body: JSON.stringify({ ...updatedPalCodeCategory })
    });

    return response;
};

const GetPalCodesByAccountantId = async (AccountantId, auth) => {

    const response = await fetch(`${PalCodes_API_URL}/ByAccountantId/${AccountantId}`, {
        method: 'GET',
        headers: {
            "Authorization": 'Bearer ' + auth,
            "Content-Type": "application/json"
        },
    });
    return response;
};

const AddPalCode = async (addPalCode, auth) => {
    const response = await fetch(`${PalCodes_API_URL}/Create`, {
        method: 'POST',
        headers: {
            "Authorization": 'Bearer ' + auth,
            "Content-Type": "application/json"
        },
        body: JSON.stringify({ ...addPalCode })
    });

    return response;
};

const UpdatePalCode = async (updatedPalCode, auth) => {
    const response = await fetch(`${PalCodes_API_URL}/Update`, {
        method: 'PUT',
        headers: {
            "Authorization": 'Bearer ' + auth,
            "Content-Type": "application/json"
        },
        body: JSON.stringify({ ...updatedPalCode })
    });

    return response;
};

const DeletePalCodeDelete = async (accountantId, PalCodeId, auth) => {
    const response = await fetch(`${PalCodes_API_URL}/Delete`, {
        method: 'Delete',
        headers: {
            "Authorization": 'Bearer ' + auth,
            "Content-Type": "application/json"
        },
        body: JSON.stringify({ accountantId, PalCodeId })
    });

    return response;
};

const DeletePalCodeCategoriesDelete = async (accountantId, PalCodeCategoryId, auth) => {
    const response = await fetch(`${PalCodeCategories_API_URL}/Delete`, {
        method: 'Delete',
        headers: {
            "Authorization": 'Bearer ' + auth,
            "Content-Type": "application/json"
        },
        body: JSON.stringify({ accountantId, PalCodeCategoryId })
    });

    return response;
};



const AccountCardPalCodeDetails = async (accountCardId, accountCardName, accountantId, palCode, caseId, startDate, endDate, isYearly, OnlyProcessed, token) => {
    try {
        const response = await fetch(`${AccountCards_API_URL}/accountCardPalcodeDetails`, {
            method: 'POST',
            headers: {
                "Authorization": 'Bearer ' + token,
                "Content-Type": "application/json",
            },
            body: JSON.stringify({ accountCardId, accountCardName, accountantId, palCode, caseId, startDate, endDate, isYearly, OnlyProcessed })
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        return response;
    } catch (error) {
    }
};

// New method using FetchType enum
const AccountCardPalCodeDetailsWithFetchType = async (accountCardIds, accountCardNames, palCodes, accountantId, caseId, startDate, endDate, isYearly, onlyProcessed, fetchType, token, filterByDocumentDate = false, currencyFilter = null, palCodeTypes = null) => {
    try {
        const response = await fetch(`${AccountCards_API_URL}/accountCardPalcodeDetails`, {
            method: 'POST',
            headers: {
                "Authorization": 'Bearer ' + token,
                "Content-Type": "application/json",
            },
            body: JSON.stringify({ 
                accountCardIds, 
                accountCardNames, 
                palCodes, 
                palCodeTypes: palCodeTypes || [],
                accountantId, 
                caseId, 
                startDate, 
                endDate, 
                isYearly, 
                onlyProcessed, 
                fetchType,
                filterByDocumentDate,
                currencyFilter
            })
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        return response;
    } catch (error) {
        console.error('Error in AccountCardPalCodeDetailsWithFetchType:', error);
        throw error;
    }
};



const GetPalCodeAccountingGroupsByAccountantId = async (AccountantId, auth) => {
    const response = await fetch(`${PalCodeAccountingGroups_API_URL}/ByAccountantId/${AccountantId}`, {
        method: 'GET',
        headers: {
            "Authorization": 'Bearer ' + auth,
            "Content-Type": "application/json"
        },
    });

    return response;
};

const AddPalCodeAccountingGroups = async (addPalCodeAccountingGroups, auth) => {
    const response = await fetch(`${PalCodeAccountingGroups_API_URL}/Create`, {
        method: 'POST',
        headers: {
            "Authorization": 'Bearer ' + auth,
            "Content-Type": "application/json"
        },
        body: JSON.stringify({ ...addPalCodeAccountingGroups })
    });

    return response;
};

const UpdatePalCodeAccountingGroups = async (updatedPalCodeAccountingGroups, auth) => {
    const response = await fetch(`${PalCodeAccountingGroups_API_URL}/Update`, {
        method: 'PUT',
        headers: {
            "Authorization": 'Bearer ' + auth,
            "Content-Type": "application/json"
        },
        body: JSON.stringify({ ...updatedPalCodeAccountingGroups })
    });

    return response;
};

const DeletePalCodeAccountingGroups = async (accountantId, AccountingGroupName, auth) => {
    const response = await fetch(`${PalCodeAccountingGroups_API_URL}/Delete`, {
        method: 'Delete',
        headers: {
            "Authorization": 'Bearer ' + auth,
            "Content-Type": "application/json"
        },
        body: JSON.stringify({ accountantId, AccountingGroupName })
    });

    return response;
};

const GetCode6111ByType = async (type, auth) => {
    const response = await fetch(`${Code6111_API_URL}/GetByType`, {
        method: 'POST',
        headers: {
            "Authorization": 'Bearer ' + auth,
            "Content-Type": "application/json"
        },
        body: JSON.stringify({ type: type })
    });

    return response;
};

const GetAllCode6111 = async (auth) => {
    const response = await fetch(`${Code6111_API_URL}/GetAll`, {
        method: 'GET',
        headers: {
            "Authorization": 'Bearer ' + auth,
            "Content-Type": "application/json"
        },
    });

    return response;
};

const GetCode6111GroupedByTitle = async (type, auth) => {
    const response = await fetch(`${Code6111_API_URL}/GetGroupedByTitle`, {
        method: 'POST',
        headers: {
            "Authorization": 'Bearer ' + auth,
            "Content-Type": "application/json"
        },
        body: JSON.stringify({ type: type })
    });
    return response;
};

/**
 * Saves Report6111 entries for a case and year. Body: { caseId, year, items: [{ code6111Id, currentAmount, previousAmount? }] }.
 */
const SaveReport6111Entries = async (body, auth) => {
    const response = await fetch(`${Code6111_API_URL}/SaveEntries`, {
        method: 'POST',
        headers: {
            "Authorization": 'Bearer ' + auth,
            "Content-Type": "application/json"
        },
        body: JSON.stringify(body)
    });
    return response;
};

/**
 * Gets saved Report6111 entries for a case and year. Query: caseId, year.
 */
const GetReport6111Entries = async (caseId, year, auth, includeZeroAmounts = false) => {
    const params = new URLSearchParams({
        caseId,
        year: String(year),
        includeZeroAmounts: String(Boolean(includeZeroAmounts))
    });
    const response = await fetch(`${Code6111_API_URL}/GetEntries?${params}`, {
        method: 'GET',
        headers: {
            "Authorization": 'Bearer ' + auth,
            "Content-Type": "application/json"
        }
    });
    return response;
};

/**
 * Gets the breakdown lines of every 6111 code for a case and year.
 * Returns: [{ id, code6111Id, name, amount, order }].
 */
const GetReport6111EntryDetails = async (caseId, year, auth) => {
    const params = new URLSearchParams({ caseId, year: String(year) });
    const response = await fetch(`${Code6111_API_URL}/GetEntryDetails?${params}`, {
        method: 'GET',
        headers: {
            "Authorization": 'Bearer ' + auth,
            "Content-Type": "application/json"
        }
    });
    return response;
};

/**
 * Replaces one code's breakdown lines and saves their sum as the code's amount.
 * Body: { caseId, year, code6111Id, previousAmount?, lines: [{ name, amount }] }.
 * Returns: { code6111Id, total, details[] }.
 */
const SaveReport6111EntryDetails = async (body, auth) => {
    const response = await fetch(`${Code6111_API_URL}/SaveEntryDetails`, {
        method: 'PUT',
        headers: {
            "Authorization": 'Bearer ' + auth,
            "Content-Type": "application/json"
        },
        body: JSON.stringify(body)
    });
    return response;
};

/**
 * Validates a 6111 report request without generating the file.
 * Body: Generate6111SingleRequest { caseId, taxYear, auditOpinion?, alternative?, ifrsYear?, reportingMethod?, accountingMethod?, bookkeepingMethod?, amountCurrency?, amountsInThousands? }.
 * Backend hydrates report lines and header details from saved entries/client data.
 * Returns: { isValid, errors[], warnings[], computedFieldsPreview? }.
 */
const ValidateReport6111 = async (body, auth) => {
    const response = await fetch(`${Code6111_API_URL}/Validate`, {
        method: 'POST',
        headers: {
            "Authorization": 'Bearer ' + auth,
            "Content-Type": "application/json"
        },
        body: JSON.stringify(body)
    });
    return response;
};

const GenerateReport6111 = async (body, auth) => {
    // body: { caseId, taxYear, auditOpinion?, alternative?, reportingMethod?, accountingMethod?, bookkeepingMethod?, amountCurrency?, amountsInThousands? }
    const response = await fetch(`${Code6111_API_URL}/Generate`, {
        method: 'POST',
        headers: {
            "Authorization": 'Bearer ' + auth,
            "Content-Type": "application/json"
        },
        body: JSON.stringify(body)
    });
    return response;
};

const GenerateReport6111Pdf = async (body, auth) => {
    // body: { caseId, taxYear, auditOpinion?, alternative?, reportingMethod?, accountingMethod?, bookkeepingMethod?, amountCurrency?, amountsInThousands? }
    const response = await fetch(`${Code6111_API_URL}/GeneratePdf`, {
        method: 'POST',
        headers: {
            "Authorization": 'Bearer ' + auth,
            "Content-Type": "application/json"
        },
        body: JSON.stringify(body)
    });
    return response;
};

// ===================== Code1214 (Form 1214) =====================

/** Returns all Code1214 catalog rows (flat). Response: Code1214ItemDto[] { id, number, name, title }. */
const GetAllCode1214 = async (auth) => {
    const response = await fetch(`${Code1214_API_URL}/GetAll`, {
        method: 'GET',
        headers: {
            "Authorization": 'Bearer ' + auth,
            "Content-Type": "application/json"
        }
    });
    return response;
};

/** Returns Code1214 catalog grouped by title. Response: [{ title, codes: [{ id, number, name, title }] }] (backend order preserved). */
const GetCode1214GroupedByTitle = async (auth) => {
    const response = await fetch(`${Code1214_API_URL}/GetGroupedByTitle`, {
        method: 'GET',
        headers: {
            "Authorization": 'Bearer ' + auth,
            "Content-Type": "application/json"
        }
    });
    return response;
};

/** Gets saved Report1214 rows for a case and year. Query: caseId, year. Response: Report1214Dto[]. */
const GetReport1214 = async (caseId, year, auth) => {
    const params = new URLSearchParams({ caseId, year: String(year) });
    const response = await fetch(`${Code1214_API_URL}/Get?${params}`, {
        method: 'GET',
        headers: {
            "Authorization": 'Bearer ' + auth,
            "Content-Type": "application/json"
        }
    });
    return response;
};

/**
 * Saves Report1214 amounts. Body: { caseId, year, items: [{ code1214Id, currentAmount }] }.
 * currentAmount null deletes the row; 0 is preserved. 200 -> Report1214Dto[]; 400 -> SaveReport1214Result.
 */
const SaveReport1214 = async (body, auth) => {
    const response = await fetch(`${Code1214_API_URL}/Save`, {
        method: 'POST',
        headers: {
            "Authorization": 'Bearer ' + auth,
            "Content-Type": "application/json"
        },
        body: JSON.stringify(body)
    });
    return response;
};

/**
 * Deletes all saved Report1214 amounts for a case and year.
 * Body: { caseId, year }. 200 -> { deletedCount }.
 */
const ClearReport1214 = async (body, auth) => {
    const response = await fetch(`${Code1214_API_URL}/ClearAll`, {
        method: 'POST',
        headers: {
            "Authorization": 'Bearer ' + auth,
            "Content-Type": "application/json"
        },
        body: JSON.stringify(body)
    });
    return response;
};

/**
 * Validates Form 1214 from backend-saved amounts. Body: { caseId, taxYear }.
 * Response: Report1214ValidationResult { isValid, errors[], warnings[], headerPreview, codeAmountPreview[], rawCodeAmountLength, finalCodeAmountLength }.
 */
const ValidateReport1214 = async (body, auth) => {
    const response = await fetch(`${Code1214_API_URL}/Validate`, {
        method: 'POST',
        headers: {
            "Authorization": 'Bearer ' + auth,
            "Content-Type": "application/json"
        },
        body: JSON.stringify(body)
    });
    return response;
};

/**
 * Generates the Form 1214 main record TXT. Body: { caseId, taxYear }.
 * 200 -> file blob (Content-Disposition filename); 400 -> Generate1214Response JSON.
 */
const GenerateReport1214 = async (body, auth) => {
    const response = await fetch(`${Code1214_API_URL}/Generate`, {
        method: 'POST',
        headers: {
            "Authorization": 'Bearer ' + auth,
            "Content-Type": "application/json"
        },
        body: JSON.stringify(body)
    });
    return response;
};

const GetPalcodesDetailsByAccountCard = async (AccountantId, PalCode, auth, PalCodes = null, FetchType = 1) => {
    // Prepare request body based on FetchType
    let requestBody = { AccountantId };
    
    if (FetchType === 1) {
        // Single PAL code - use PalCode
        requestBody.PalCode = PalCode;
        requestBody.FetchType = 1;
    } else if (FetchType === 2) {
        // All PAL codes - no specific PAL codes needed
        requestBody.FetchType = 2;
    } else if (FetchType === 3) {
        // Multiple PAL codes - use PalCodes array
        requestBody.PalCodes = PalCodes || (PalCode ? [PalCode] : []);
        requestBody.FetchType = 3;
    } else {
        // Default to single
        requestBody.PalCode = PalCode;
        requestBody.FetchType = 1;
    }

    const response = await fetch(`${AccountCards_API_URL}/GetPalcodesDetailsByAccountCard`, {
        method: 'POST',
        headers: {
            "Authorization": 'Bearer ' + auth,
            "Content-Type": "application/json"
        },
        body: JSON.stringify(requestBody)
    });

    return response;
};

/**
 * POST /ClientsSummary.pdf
 * @param {object}  dto  – the filter object sent to the API
 * @param {string}  auth – JWT bearer token
 * @returns {Promise<Response>}
 */
export const DownloadClientsSummaryPdf = async (dto, auth) => {
    return fetch(`${Accountants_API_URL}/ClientsSummary.pdf`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${auth}`,
        },
        body: JSON.stringify(dto),
    });
};

/**
 * POST /ClientsSummary.xlsx
 * @param {object}  dto  – the filter object sent to the API
 * @param {string}  auth – JWT bearer token
 * @returns {Promise<Response>}
 */
export const DownloadClientsSummaryExcel = async (dto, auth) => {
    return fetch(`${Accountants_API_URL}/ClientsSummary.xlsx`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${auth}`,
        },
        body: JSON.stringify(dto),
    });
};




const GetAllTBanksByCaseId = async (caseId, auth) => {
    try {
        const response = await fetch(`${TBanks_API_URL}/GetAllByCaseId/${caseId}`, {
            method: 'GET',
            headers: {
                "Authorization": `Bearer ${auth}`,
                "Content-Type": "application/json"
            },
        });

        if (!response.ok) {
            throw new Error('Failed to fetch TBanks by CaseId');
        }

        const data = await response.json();
        return data;
    } catch (error) {
        console.error('Error fetching TBanks:', error);
        throw error; // Re-throw the error to handle it further up the call chain
    }
};

const AddTBank = async (CaseId, BankName, BankCode, BranchNumber, AccountNumber, ForeignCurrencyCode, CardID, auth) => {
    
    const response = await fetch(`${TBanks_API_URL}/Create`, {
        method: 'POST',
        headers: {
            "Authorization": 'Bearer ' + auth,
            "Content-Type": "application/json"
        },
        body: JSON.stringify({ CaseId, BankName, BankCode, BranchNumber, ForeignCurrencyCode, AccountNumber, CardID })
    });

    return response;
};

export const DeleteTBank = async (caseId, accountNumber, bankCode, authToken) => {
    const response = await fetch(`${TBanks_API_URL}/Delete`, {
        method: 'DELETE',
        headers: {
            "Authorization": 'Bearer ' + authToken,
            'Content-Type': 'application/json' // Content type is JSON
        },
        body: JSON.stringify({ caseId, accountNumber, bankCode }) // Send the caseId, accountNumber, and bankCode
    });

    // Check if the request was successful
    return response;
};

const EditTBank = async (CaseId, OldBankCode, OldAccountNumber, BankName, BankCode, BranchNumber, AccountNumber, ForeignCurrencyCode,CardId, auth) => {

    const response = await fetch(`${TBanks_API_URL}/Update`, {
        method: 'PUT',  // Use PUT for updates
        headers: {
            "Authorization": 'Bearer ' + auth,
            "Content-Type": "application/json"
        },
        body: JSON.stringify({
            CaseId,
            BankName,
            BankCode,
            BranchNumber,
            AccountNumber,
            OldBankCode,      // Old bank code for identifying the record to update
            OldAccountNumber,  // Old account number for identifying the record to update
            ForeignCurrencyCode,
            CardId
        })
    });

    return response;
};

const GetCollectionPage = async (caseId, auth, date = null, filterMode = null, filterStartDate = null, filterEndDate = null) => {
    try {
        const response = await fetch(`${Ledgers_API_URL}/GetByCaseId`, {
            method: 'POST',
            headers: {
                "Authorization": 'Bearer ' + auth,
                "Content-Type": "application/json"
            },
            body: JSON.stringify({
                caseId: caseId,
                date: date, // Send date as provided, or null if not
                filterMode: filterMode, // 'tillDate' or 'monthOnly'
                filterStartDate: filterStartDate,
                filterEndDate: filterEndDate
            })
        });
        return response;
    } catch (error) {
        console.error('Failed to get Collection Page');
        throw error;
    }
};

export const GetCollectionPageBulk = async (caseIds, auth, date = null, getClosed = false, filterMode = null, filterStartDate = null, filterEndDate = null, mixPayments = false) => {
    try {
        return await fetch(`${Ledgers_API_URL}/GetByCases`, {
            method: 'POST',
            headers: {
                Authorization: `Bearer ${auth}`,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ 
                caseIds, 
                date, 
                getClosed,
                mixPayments,
                filterMode,
                filterStartDate,
                filterEndDate
            }),
        });
    } catch (err) {
        console.error('Failed to get Collection Page (bulk)', err);
        throw err;
    }
};

const GetCollectionPageForAllAccountantClients = async (
    accountantId,
    authToken,
    date = null,
    getClosed = false,
    filterMode = null,
    filterStartDate = null,
    filterEndDate = null,
    mixPayments = false
) => {
    try {
        const res = await fetch(`${Ledgers_API_URL}/GetForAllAccountantClients`, {
            method: 'POST',
            headers: {
                Authorization: 'Bearer ' + authToken,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ 
                accountantId, 
                date, 
                getClosed,
                mixPayments,
                filterMode,
                filterStartDate,
                filterEndDate
            }),
        });
        return res; // caller decides .ok / .json()
    } catch (err) {
        console.error('Failed GetForAllAccountantClients', err);
        throw err;
    }
};


const AddMonthlyCharge = async (ledgerId, startDate, endDate, amount, description, caseId, auth) => {
    const response = await fetch(`${Ledgers_API_URL}/monthlyCharge/create`, {
        method: 'POST',
        headers: {
            "Authorization": 'Bearer ' + auth,
            "Content-Type": "application/json"
        },
        body: JSON.stringify({ ledgerId, startDate, endDate, amount, description, caseId })
    });

    return response;
};

/**
 * Downloads the monthly-charges import template (Hebrew columns, RTL) from the API
 * and triggers a browser download.
 * @param {string} auth - Bearer token
 */
const DownloadMonthlyChargesTemplate = async (auth) => {
    const response = await fetch(`${Ledgers_API_URL}/monthlyCharge/import/template`, {
        method: 'GET',
        headers: { "Authorization": 'Bearer ' + auth },
    });
    if (!response.ok) throw new Error('Failed to download monthly charges template');
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'MonthlyChargesTemplate.xlsx');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
};

/**
 * Uploads a monthly-charges Excel file and returns a validated preview. Rows without a
 * caseId are resolved by client name on the server; unresolved rows are flagged.
 * @param {string} accountantId
 * @param {File} file
 * @param {string} auth - Bearer token
 * @param {string|null} defaultCaseId - fallback caseId (non-leading business, currently open client)
 * @returns {Promise<Object>} - { totalLines, validCount, errorCount, unresolvedCaseIdCount, rows, errorMessages }
 */
const PreviewMonthlyChargesImport = async (accountantId, file, auth, defaultCaseId = null) => {
    const formData = new FormData();
    formData.append('AccountantId', accountantId);
    formData.append('File', file);
    if (defaultCaseId) formData.append('DefaultCaseId', defaultCaseId);

    const response = await fetch(`${Ledgers_API_URL}/monthlyCharge/import/preview`, {
        method: 'POST',
        headers: { "Authorization": 'Bearer ' + auth },
        body: formData
    });
    if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || 'Failed to preview monthly charges import');
    }
    return await response.json();
};

/**
 * Commits the (already resolved) monthly-charge rows produced by the preview step.
 * @param {string} accountantId
 * @param {Array<Object>} rows - preview rows (with caseId filled in for previously-unresolved rows)
 * @param {string} auth - Bearer token
 * @returns {Promise<Object>} - { processedCount, errorCount, skippedCount, errorMessages }
 */
const CommitMonthlyChargesImport = async (accountantId, rows, auth) => {
    const response = await fetch(`${Ledgers_API_URL}/monthlyCharge/import/commit`, {
        method: 'POST',
        headers: {
            "Authorization": 'Bearer ' + auth,
            "Content-Type": "application/json"
        },
        body: JSON.stringify({ accountantId, rows })
    });
    if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || 'Failed to import monthly charges');
    }
    return await response.json();
};

const AddOneTimeCharge = async (ledgerId, date, amount, description, caseId, auth) => {

    const response = await fetch(`${Ledgers_API_URL}/oneTimeCharge/create`, {
        method: 'POST',
        headers: {
            "Authorization": 'Bearer ' + auth,
            "Content-Type": "application/json"
        },
        body: JSON.stringify({ ledgerId, date, amount, description, caseId })
    });

    return response;
};

const AddPayment = async (ledgerId, type, date, amount, description, reference, caseId, bankName, bankCode, bankBrabankBranchCode, supplierAccountNumber, auth) => {

    const response = await fetch(`${Ledgers_API_URL}/payment/create`, {
        method: 'POST',
        headers: {
            "Authorization": 'Bearer ' + auth,
            "Content-Type": "application/json"
        },
        body: JSON.stringify({ ledgerId, type, date, amount, description, reference, caseId, bankName, bankCode, bankBrabankBranchCode, supplierAccountNumber })
    });

    return response;
};

// Edit Monthly Charge
const EditMonthlyCharge = async (id, ledgerId, startDate, endDate, amount, description, auth) => {
    const response = await fetch(`${Ledgers_API_URL}/monthlyCharge/update`, {
        method: 'PUT',
        headers: {
            "Authorization": 'Bearer ' + auth,
            "Content-Type": "application/json"
        },
        body: JSON.stringify({ id, ledgerId, startDate, endDate, amount, description })
    });
    return response;
};

// Edit One-Time Charge
const EditOneTimeCharge = async (id, ledgerId, date, amount, description, auth) => {
    const response = await fetch(`${Ledgers_API_URL}/oneTimeCharge/update`, {
        method: 'PUT',
        headers: {
            "Authorization": 'Bearer ' + auth,
            "Content-Type": "application/json"
        },
        body: JSON.stringify({ id, ledgerId, date, amount, description })
    });
    return response;
};

// Edit Payment
const EditPayment = async (id, ledgerId, type, date, amount, description, reference, bankName, bankCode, bankBrabankBranchCode, supplierAccountNumber, auth) => {

    const response = await fetch(`${Ledgers_API_URL}/payment/update`, {
        method: 'PUT',
        headers: {
            "Authorization": 'Bearer ' + auth,
            "Content-Type": "application/json"
        },
        body: JSON.stringify({ id, ledgerId, type, date, amount, description, reference, bankName, bankCode, bankBrabankBranchCode, supplierAccountNumber })
    });
    return response;
};

// Delete Monthly Charge
const DeleteMonthlyCharge = async (monthlyChargeId, auth) => {
    const response = await fetch(`${Ledgers_API_URL}/monthlyCharge/delete/${monthlyChargeId}`, {
        method: 'DELETE',
        headers: {
            "Authorization": 'Bearer ' + auth,
            "Content-Type": "application/json"
        }
    });
    return response;
};

// Delete One-Time Charge
const DeleteOneTimeCharge = async (oneTimeChargeId, auth) => {
    const response = await fetch(`${Ledgers_API_URL}/oneTimeCharge/delete/${oneTimeChargeId}`, {
        method: 'DELETE',
        headers: {
            "Authorization": 'Bearer ' + auth,
            "Content-Type": "application/json"
        }
    });
    return response;
};

// Delete Payment
const DeletePayment = async (paymentId, auth) => {
    const response = await fetch(`${Ledgers_API_URL}/payment/delete/${paymentId}`, {
        method: 'DELETE',
        headers: {
            "Authorization": 'Bearer ' + auth,
            "Content-Type": "application/json"
        }
    });
    return response;
};

// Mark payment as canceled manually (register as canceled without canceling the document)
const CancelPaymentManually = async (paymentId, auth) => {
    const response = await fetch(`${Ledgers_API_URL}/payment/cancelManually/${paymentId}`, {
        method: 'PUT',
        headers: {
            "Authorization": 'Bearer ' + auth,
            "Content-Type": "application/json"
        }
    });
    return response;
};

// Set payment amount to negative to exclude from calculations (still shown in list)
const SetPaymentAmountNegative = async (paymentId, auth) => {
    const response = await fetch(`${Ledgers_API_URL}/payment/setAmountNegative/${paymentId}`, {
        method: 'PUT',
        headers: {
            "Authorization": 'Bearer ' + auth,
            "Content-Type": "application/json"
        }
    });
    return response;
};

const DownloadLedgerReport = async (caseId, date, auth) => {
    try {
        const response = await fetch(`${Ledgers_API_URL}/DownloadLedgerReport`, {
            method: 'POST',
            headers: {
                "Authorization": 'Bearer ' + auth,
                "Content-Type": "application/json"
            },
            body: JSON.stringify({
                CaseId: caseId,
                Date: date
            })
        });

        if (!response.ok) {
            const errorText = await response.text();
            console.error(errorText);
            throw new Error(errorText);
        }

        const blob = await response.blob();

        // Create a URL for the PDF file
        const fileUrl = window.URL.createObjectURL(blob);

        // Create a temporary link to trigger the download
        const link = document.createElement('a');
        link.href = fileUrl;
        link.setAttribute('download', `תנועות-כספיות-${caseId}.pdf`); // Filename in Hebrew or desired format
        document.body.appendChild(link);
        link.click();

        // Cleanup by removing the link and revoking the object URL
        document.body.removeChild(link);
        window.URL.revokeObjectURL(fileUrl);
    } catch (error) {
        console.error("Error downloading ledger report:", error);
        throw error;
    }
};

const GetBalancesByCaseIds = async (accountantId, caseIds, auth, date = null) => {

    try {
        const response = await fetch(`${Ledgers_API_URL}/GetBalancesByCaseIds`, {
            method: 'POST',
            headers: {
                "Authorization": `Bearer ${auth}`,
                "Content-Type": "application/json"
            },
            body: JSON.stringify({ accountantId, caseIds, date })
        });

        if (!response.ok) {
            throw new Error(`Error fetching balances: ${response.statusText}`);
        }

        const data = await response.json();
        return data;
    } catch (error) {
        console.error('Failed to get balances by case IDs:', error);
        return []; // Re-throw to handle further up the call chain if necessary
    }
};




const ExportAccountCards = async (CaseId, AccountantId, auth, SelectedCardIds = null) => {
    try {
        const requestBody = { CaseId, AccountantId };
        
        // Include selected card IDs if provided
        if (SelectedCardIds && SelectedCardIds.length > 0) {
            requestBody.SelectedCardIds = SelectedCardIds;
        }
        
        const response = await fetch(`${AccountCards_API_URL}/ExportAccountCards`, {
            method: 'POST',
            headers: {
                "Authorization": `Bearer ${auth}`,
                "Content-Type": "application/json"
            },
            body: JSON.stringify(requestBody)
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const blob = await response.blob();
        const fileUrl = window.URL.createObjectURL(blob);

        // Create a temporary link to trigger the download
        const link = document.createElement('a');
        link.href = fileUrl;
        link.setAttribute('download', `Account_Cards_${CaseId}.xlsx`);
        document.body.appendChild(link);
        link.click();

        // Cleanup
        document.body.removeChild(link);
        window.URL.revokeObjectURL(fileUrl);
    } catch (error) {
        console.error("Error exporting account cards:", error);
        throw error;
    }
};

const MoveTransactionsBetweenCards = async (CaseId, OldCardId, NewCardId, auth) => {
    const response = await fetch(`${AccountCards_API_URL}/MoveTransactions`, {
        method: 'POST',
        headers: {
            "Authorization": `Bearer ${auth}`,
            "Content-Type": "application/json"
        },
        body: JSON.stringify({ CaseId, OldCardId, NewCardId })
    });

    return response;
};

const BulkMoveTransactionsBetweenCards = async (CaseId, Migrations, auth) => {
    const response = await fetch(`${AccountCards_API_URL}/BulkMoveTransactions`, {
        method: 'POST',
        headers: {
            "Authorization": `Bearer ${auth}`,
            "Content-Type": "application/json"
        },
        body: JSON.stringify({ CaseId, Migrations })
    });

    return response;
};

const CommitAccountCardsUpload = async (caseId, file, auth) => {
    const formData = new FormData();
    formData.append("CaseId", caseId);
    formData.append("File", file);

    const response = await fetch(`${AccountCards_API_URL}/CommitUpload`, {
        method: 'POST',
        headers: {
            "Authorization": `Bearer ${auth}`,
        },
        body: formData
    });

    return response;
};

const GenerateLedgerInvoicesDraft = async (caseId, payments, invoiceSignature = null, invoiceReference = null, invoiceGenDate, auth) => {

    const response = await fetch(`${Ledgers_API_URL}/GenerateLedgerInvoicesDraft`, {
        method: 'POST',
        headers: {
            Authorization: 'Bearer ' + auth,
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({ caseId, payments, Signature: invoiceSignature, Reference: invoiceReference, InvoiceGenerateDate: invoiceGenDate }),
    });

    return response;   // caller handles .ok / .json()
};


const GenerateLedgerInvoices = async (caseId, payments, invoiceSignature = null, invoiceReference = null, invoiceGenDate, auth, decisionFlag = null) => {

    const response = await fetch(`${Ledgers_API_URL}/GenerateLedgerInvoices`, {
        method: 'POST',
        headers: {
            Authorization: 'Bearer ' + auth,
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
            caseId, 
            payments, 
            Signature: invoiceSignature, 
            Reference: invoiceReference, 
            InvoiceGenerateDate: invoiceGenDate,
            Decision: decisionFlag
        }),
    });

    return response;   // caller handles .ok / .json()
};

/* ────────────────  NEW HELPER  ──────────────── */
const GenerateReceiptFromLedgerPaymentDraft = async (
    caseId,              // main business/accountant caseId
    clientToPayCaseId,   // client caseId where payment was created
    ledgerId,            // ledger that holds the payment
    paymentId,           // the payment you just inserted
    invoiceSignature = null,
    invoiceReference = null,
    authToken,
    receiptDate = null
) => {
    return await fetch(`${PaymentAcceptance_API_URL}/GenerateReceiptFromLedgerPaymentDraft`, {
        method: 'POST',
        headers: {
            Authorization: 'Bearer ' + authToken,
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
            caseId, 
            clientToPayCaseId,
            ledgerId, 
            paymentId, 
            Signature: invoiceSignature, 
            Reference: invoiceReference, 
            receiptDate: receiptDate 
        }),
    });
};

const GenerateReceiptFromLedgerPayment = async (
    caseId,      // client caseId (the ledger owner)
    ledgerId,    // ledger that holds the payment
    paymentId,   // the payment you just inserted
    invoiceSignature = null,
    invoiceReference = null,
    authToken

) => {
    return await fetch(`${PaymentAcceptance_API_URL}/GenerateReceiptFromLedgerPayment`, {
        method: 'POST',
        headers: {
            Authorization: 'Bearer ' + authToken,
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({ caseId, ledgerId, paymentId, Signature: invoiceSignature, Reference: invoiceReference }),
    });
};

const CloseSelectedPayments = async (accountantId, payments, auth) => {
    return await fetch(`${Ledgers_API_URL}/payment/closeSelected`, {
        method: 'PUT',
        headers: {
            Authorization: 'Bearer ' + auth,
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({ accountantId, payments }),
    });
};


const OpenSelectedPayments = async (accountantId, payments, auth) => {
    return await fetch(`${Ledgers_API_URL}/payment/openPayment`, {
        method: 'PUT',
        headers: {
            Authorization: 'Bearer ' + auth,
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({ accountantId, payments }),
    });
};


const GetClientCaseIds = async (AccountantId, CaseId, auth) => {
    const response = await fetch(`${Accountants_API_URL}/ClientCaseIds`, {
        method: 'POST',
        headers: {
            "Authorization": `Bearer ${auth}`,
            "Content-Type": "application/json"
        },
        body: JSON.stringify({ AccountantId, CaseId })
    });
    return response;
};

const DownloadLedgerReportBulk = async (caseIds, asOfDate, auth) => {
    try {
        const resp = await fetch(`${Ledgers_API_URL}/DownloadLedgerReportBulk`, {
            method: 'POST',
            headers: {
                Authorization: 'Bearer ' + auth,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ caseIds, date: asOfDate }),     // DTO fields
        });

        if (!resp.ok) {
            const msg = await resp.text();
            throw new Error(msg || 'DownloadLedgerReportBulk failed');
        }

        /* ------- save the PDF locally (same pattern you already use) ------- */
        const blob = await resp.blob();
        const url = window.URL.createObjectURL(blob);

        const link = document.createElement('a');
        link.href = url;
        link.download = `תנועות-כספיות-מרוכז-${new Date().toISOString().slice(0, 10)}.pdf`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
    } catch (err) {
        console.error(err);
        throw err;
    }
};

const AddMultiplePayments = async (multiplePaymentsDto, auth) => {
    const response = await fetch(`${Ledgers_API_URL}/payment/createMultiple`, {
        method: 'POST',
        headers: {
            "Authorization": 'Bearer ' + auth,
            "Content-Type": "application/json"
        },
        body: JSON.stringify(multiplePaymentsDto)
    });

    return response;
};

const GenerateReceiptFromMultiplePaymentsDraft = async (
    caseId,
    clientToPayCaseId,
    paymentIds,
    invoiceSignature = null,
    invoiceReference = null,
    authToken,
    receiptDate = null
) => {
    const response = await fetch(`${PaymentAcceptance_API_URL}/GenerateReceiptFromMultiplePaymentsDraft`, {
        method: 'POST',
        headers: {
            "Authorization": 'Bearer ' + authToken,
            "Content-Type": "application/json"
        },
        body: JSON.stringify({
            caseId: caseId,
            clientToPayCaseId: clientToPayCaseId,
            paymentIds: paymentIds,
            signature: invoiceSignature,
            reference: invoiceReference,
            receiptDate: receiptDate
        })
    });

    return response;
};

const GenerateReceiptFromMultiplePaymentsDraftBulk = async (
    caseId,
    requests,
    invoiceSignature = null,
    invoiceReference = null,
    authToken,
    receiptDate = null
) => {
    const response = await fetch(`${PaymentAcceptance_API_URL}/GenerateReceiptFromMultiplePaymentsDraftBulk`, {
        method: 'POST',
        headers: {
            "Authorization": 'Bearer ' + authToken,
            "Content-Type": "application/json"
        },
        body: JSON.stringify({
            caseId,
            requests,
            signature: invoiceSignature,
            reference: invoiceReference,
            receiptDate
        })
    });

    return response;
};

const GenerateReceiptFromMultiplePayments = async (
    caseId,
    clientToPayCaseId,
    paymentIds,
    invoiceSignature = null,
    invoiceReference = null,
    authToken
) => {
    const response = await fetch(`${PaymentAcceptance_API_URL}/GenerateReceiptFromMultiplePayments`, {
        method: 'POST',
        headers: {
            "Authorization": 'Bearer ' + authToken,
            "Content-Type": "application/json"
        },
        body: JSON.stringify({
            caseId: caseId,
            clientToPayCaseId: clientToPayCaseId,
            paymentIds: paymentIds,
            signature: invoiceSignature,
            reference: invoiceReference
        })
    });

    return response;
};

const GenerateReceiptFromMultiplePaymentsBulk = async (
    caseId,
    requests,
    invoiceSignature = null,
    invoiceReference = null,
    authToken,
    receiptDate = null
) => {
    const response = await fetch(`${PaymentAcceptance_API_URL}/GenerateReceiptFromMultiplePaymentsBulk`, {
        method: 'POST',
        headers: {
            "Authorization": 'Bearer ' + authToken,
            "Content-Type": "application/json"
        },
        body: JSON.stringify({
            caseId,
            requests,
            signature: invoiceSignature,
            reference: invoiceReference,
            receiptDate
        })
    });

    return response;
};

const GeneratePdfFromHtml = async (htmlContent, caseId, auth) => {
    try {
        const response = await fetch(`${AccountCards_API_URL}/GeneratePdfFromHtml`, {
            method: 'POST',
            headers: {
                "Authorization": `Bearer ${auth}`,
                "Content-Type": "application/json"
            },
            body: JSON.stringify({
                Html: htmlContent,
                CaseId: caseId
            })
        });

        if (!response.ok) {
            const errorText = await response.text();
            console.error(errorText);
            throw new Error(errorText);
        }

        return response;
    } catch (error) {
        console.error("Error generating PDF from HTML:", error);
        throw error;
    }
};

const CheckSortCodeUsage = async (AccountantId, CodeId, auth) => {
    const response = await fetch(`${SortCodes_API_URL}/CheckUsage`, {
        method: 'POST',
        headers: {
            "Authorization": 'Bearer ' + auth,
            "Content-Type": "application/json"
        },
        body: JSON.stringify({ AccountantId, CodeId })
    });

    return response;
};

const UploadAccountCardDocument = async (file, caseId, cardId, token, customFileName = null, year = null) => {
    const formData = new FormData();
    formData.append("File", file);
    formData.append("CaseId", caseId);
    formData.append("CardId", cardId);
    if (customFileName) {
        formData.append("CustomFileName", customFileName);
    }
    if (year !== null && year !== undefined && year !== "") {
        formData.append("Year", year);
    }

    const resp = await fetch(`${AccountCards_API_URL}/UploadDocument`, {
        method: "POST",
        headers: {
            Authorization: `Bearer ${token}`,
        },
        body: formData,
    });

    const json = await resp.json();
    if (!resp.ok) {
        throw new Error(json.error || "Failed to upload document");
    }

    // Backend now returns `{ key, url }`. The DB stores `key` (clean S3 key), and `url`
    // is a short-lived presigned URL only suitable for immediate preview.
    // Be backwards-compatible with the old shape (a plain string URL) just in case.
    if (typeof json.data === "string") {
        return { key: json.data, url: json.data };
    }
    return json.data;
};

const DeleteAccountCardDocument = async (documentUrl, caseId, cardId, token) => {
    const resp = await fetch(
        `${AccountCards_API_URL}/DeleteDocument?documentUrl=${encodeURIComponent(documentUrl)}&caseId=${caseId}&cardId=${cardId}`,
        {
            method: "DELETE",
            headers: {
                Authorization: `Bearer ${token}`,
            },
        }
    );

    if (!resp.ok) {
        const text = await resp.text();
        throw new Error(`Failed to delete document: ${text}`);
    }

    const json = await resp.json();
    return json.data || json.message;
};

const EnumPage_API_URL = `${baseURL}/EnumPage`;

const GetEnumPageData = async (caseId, accountantId, auth) => {
    const response = await fetch(`${EnumPage_API_URL}/GetEnumPageData`, {
        method: 'POST',
        headers: {
            "Authorization": 'Bearer ' + auth,
            "Content-Type": "application/json"
        },
        body: JSON.stringify({ CaseId: caseId, AccountantId: accountantId })
    });

    return response;
};

const EnumsService = {
    GetClientCaseIds,
    GetAccountCardsByCaseId,
    GetReservedAccountCardIds,
    GetAccountCardByCardId,
    GetNextCardId,
    GetCaseTypesByByAccountantId,
    GetChecksTypesByAccountantId,
    GetOtherDocumentsTypesByAccountantId,
    GetProvidersBycaseId,
    GetSortCodesByAccountantId,
    AddProvider,
    UpdateProvider,
    UpdateAccountCard,
    UpdateCaseType,
    UpdateCheckType,
    UpdateOtherDocumentsType,
    UpdateSortCode,
    MergeSortCodes,
    BatchMergeSortCodes,
    AddAccountCard,
    AddCaseType,
    AddChecksType,
    AddOtherDocumentsType,
    GetOtherDocumentsTitlesByAccountantId,
    AddOtherDocumentsTitle,
    UpdateOtherDocumentsTitle,
    DeleteOtherDocumentsTitle,
    AssignTypesToOtherDocumentsTitle,
    SetOtherDocumentsTitlesHidden,
    EnsurePermanentFileTitle,
    AddSortCode,
    DeleteAccountCardsbyCaseId,
    BulkDeleteAccountCards,
    DeleteProviderbyCaseId,
    DeleteCaseTypebyId,
    DeleteChecksTypebyId,
    DeleteOtherDocumentbyId,
    DeleteOtherDocumentsType,
    GetOtherDocumentsTypeUsage,
    DeleteSortCodebyId,
    UploadAccountCardsFileData,
    UploadFileData,
    GetClientItemsByCaseId,
    AddClientItems,
    UpdateClientItem,
    GetSortCodesGroupByAccountantId,
    AddSortCodeGroup,
    UpdateSortCodeGroup,
    DeleteSortCodeGroupById,
    GetSortCodeByName,
    // PalCodeCategories functions
    GetPalCodeCategoriesByAccountantId,
    AddPalCodeCategory,
    UpdatePalCodeCategory,

    // PalCodes functions
    GetPalCodesByAccountantId,
    AddPalCode,
    UpdatePalCode,
    DeletePalCodeDelete,
    DeletePalCodeCategoriesDelete,
    //palcode details
    AccountCardPalCodeDetails,
    AccountCardPalCodeDetailsWithFetchType,
    GetPalCodeAccountingGroupsByAccountantId,
    AddPalCodeAccountingGroups,
    UpdatePalCodeAccountingGroups,
    DeletePalCodeAccountingGroups,
    GetPalcodesDetailsByAccountCard,
    // Code6111 functions
    GetCode6111ByType,
    GetAllCode6111,
    GetCode6111GroupedByTitle,
    SaveReport6111Entries,
    GetReport6111Entries,
    GetReport6111EntryDetails,
    SaveReport6111EntryDetails,
    ValidateReport6111,
    GenerateReport6111,
    GenerateReport6111Pdf,
    // Code1214 functions
    GetAllCode1214,
    GetCode1214GroupedByTitle,
    GetReport1214,
    SaveReport1214,
    ClearReport1214,
    ValidateReport1214,
    GenerateReport1214,
    MergeAccountCards,
    BatchMergeAccountCards,
    GetPalCodePrefixes,
    SetPalCodePrefix,
    GetReservedAccountCardIdsByCase,
    GetAccountCardIdsWithLinkedDocuments,
    GetAccountCardsWithoutTransactions,
    GetCaseSystemAccountSettings,
    UpdateCaseSystemAccountSettings,
    GetMergeSuggestions,
    GetSimilarCards,
    SetAccountCardEditable,
    MoveTransactionsBetweenCards,
    BulkMoveTransactionsBetweenCards,
    CommitAccountCardsUpload,
    EditInBulk,
    EditSortCodeInBulk,
    EditDeductionInBulk,
    EditPaymentTermsInBulk,
    GetAllTBanksByCaseId,
    AddTBank,
    EditTBank,
    DeleteTBank,
    GetCollectionPage,
    AddMonthlyCharge,
    DownloadMonthlyChargesTemplate,
    PreviewMonthlyChargesImport,
    CommitMonthlyChargesImport,
    AddOneTimeCharge,
    AddPayment,
    EditMonthlyCharge,
    EditOneTimeCharge,
    EditPayment,
    DeleteMonthlyCharge,
    DeleteOneTimeCharge,
    DeletePayment,
    CancelPaymentManually,
    SetPaymentAmountNegative,
    DownloadLedgerReport,
    GetBalancesByCaseIds,
    UpdateAccountCardFewDetails,
    GetOtherDocumentsCountsByCaseId,
    UpdateAccountCardCaseId,
    UpdateAccountCardBankDetails,
    ExportAccountCards,
    GetCollectionPageBulk,
    GetCollectionPageForAllAccountantClients,
    GenerateLedgerInvoicesDraft,
    GenerateLedgerInvoices,
    GenerateReceiptFromLedgerPaymentDraft,
    GenerateReceiptFromLedgerPayment,
    GenerateReceiptFromMultiplePaymentsDraft,
    GenerateReceiptFromMultiplePaymentsDraftBulk,
    GenerateReceiptFromMultiplePayments,
    GenerateReceiptFromMultiplePaymentsBulk,
    DownloadClientsSummaryPdf,
    DownloadClientsSummaryExcel,
    CloseSelectedPayments,
    OpenSelectedPayments,
    DownloadLedgerReportBulk,
    AddMultiplePayments,
    GeneratePdfFromHtml,
    CheckSortCodeUsage,
    UploadAccountCardDocument,
    DeleteAccountCardDocument,
    GetEnumPageData,
};

export default EnumsService;